import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Button, CheckBox, DataTable, Layer, Pagination, Spinner, Tip } from 'grommet';
import { Close, Dashboard, SettingsOption, StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { SearchInput } from '../forms/SearchInput';
import { ApiError, EquipmentService } from '/src/api';
import { AddButton, DataTableHeader, DataTableItem, EntityIcon, FormCard, InfoBox, Link, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Equipment, Facility, ModelName, TEquipmentId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { EntityStatus, TResponseMetadata } from '/src/lib/types';
import { formatNumber, getPeriodFromDateString } from '/src/utils';

export type DeleteMultipleEquipmentModalProps = {
  facility: Facility;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
  isCanada?: boolean;
};

export const DeleteMultipleEquipmentModal: React.FC<DeleteMultipleEquipmentModalProps> = observer(
  ({ facility, isVisible, setIsVisible, setShouldRefresh, isCanada = false }) => {
    /** Context **/
    const userStore = useUserStore();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();
    const tooltips = tooltipMessages();

    /** State **/
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
    const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<TEquipmentId[]>([]);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(20);
    const [metadata, setMetadata] = useState<TResponseMetadata>();
    const [searchQuery, setSearchQuery] = useState('');
    const [committedSearchQuery, setCommittedSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    /** Computed **/
    const canDelete = userStore.isFuseAdmin;

    const currentUsagePeriod = equipmentList?.[0]?.current_reporting_period_equipment_usage?.start_reporting_quarter;

    const selectedEquipmentIdSet = new Set(selectedEquipmentIds);
    const selectedOnPageCount = equipmentList.filter((eq) => selectedEquipmentIdSet.has(eq.id)).length;
    const isPageSelected = !!equipmentList.length && selectedOnPageCount === equipmentList.length;
    const isPagePartiallySelected = selectedOnPageCount > 0 && selectedOnPageCount < equipmentList.length;
    const hasSelection = !!selectedEquipmentIds.length;

    /** Methods **/
    const search = (query: string) => {
      setSearchQuery(query);
      setCommittedSearchQuery(query);
      setIsSearching(query !== committedSearchQuery);
      setCurrentPage(1);
    };

    const loadEquipment = async (page = 1, serialNumber?: string) => {
      try {
        setIsLoading(true);
        const { data, meta } = await EquipmentService.list({
          facility_id: facility.id,
          is_active: true,
          is_finalized: false,
          page,
          limit,
          ...(serialNumber ? { serial_number: serialNumber } : {}),
        });

        setEquipmentList(data);
        setMetadata(meta);
        setCurrentPage(meta?.current_page ?? page);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.listEquipment.error);
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    };

    const deleteEquipment = async () => {
      if (isDeleting || !selectedEquipmentIds.length) return;

      try {
        setIsDeleting(true);

        await EquipmentService.delete({ equipment_ids: selectedEquipmentIds });

        setShouldRefresh(true);
        setIsVisible(false);
        toast.success(toastMessages.deleteEquipment.success);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.deleteEquipment.error);
      } finally {
        setIsDeleting(false);
      }
    };

    const toggleSelection = (equipmentId: TEquipmentId) => {
      setSelectedEquipmentIds((prev) =>
        prev.includes(equipmentId) ? prev.filter((id) => id !== equipmentId) : [...prev, equipmentId]
      );
    };

    const toggleSelectPage = (checked: boolean) => {
      const pageIds = equipmentList.map((eq) => eq.id);
      const pageIdSet = new Set(pageIds);
      setSelectedEquipmentIds((prev) => {
        const offPageIds = prev.filter((id) => !pageIdSet.has(id));
        return checked ? [...offPageIds, ...pageIds] : offPageIds;
      });
    };

    const selectAll = async () => {
      if (isSelectingAll || !metadata?.total) return;

      try {
        setIsSelectingAll(true);
        const { data } = await EquipmentService.list({
          facility_id: facility.id,
          is_active: true,
          is_finalized: false,
          page: 1,
          limit: metadata.total,
          ...(committedSearchQuery ? { serial_number: committedSearchQuery } : {}),
        });

        setSelectedEquipmentIds((prev) => Array.from(new Set([...prev, ...data.map((eq) => eq.id)])));
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.listEquipment.error);
      } finally {
        setIsSelectingAll(false);
      }
    };

    /** Effects **/
    useEffect(() => {
      if (isVisible) {
        setSelectedEquipmentIds([]);
        setIsConfirmed(false);
        setCurrentPage(1);
        setSearchQuery('');
        setCommittedSearchQuery('');
      }
    }, [isVisible, facility.id]);

    useEffect(() => {
      if (!canDelete && isVisible) setIsVisible(false);
    }, [canDelete, isVisible]);

    useEffect(() => {
      if (isVisible && currentPage) {
        loadEquipment(currentPage, committedSearchQuery || undefined);
      }
    }, [isVisible, currentPage, committedSearchQuery]);

    /** Render **/
    const renderDataTable = () => {
      return (
        <Box elevation="small" round="xsmall" overflow="hidden">
          <DataTable
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
            primaryKey="id"
            columns={[
              {
                property: 'select',
                size: '3%',
                header: (
                  <CheckBox
                    checked={isPageSelected}
                    indeterminate={isPagePartiallySelected}
                    onChange={(e) => toggleSelectPage(e.target.checked)}
                  />
                ),
                render: (equipment: Equipment) => (
                  <CheckBox
                    checked={selectedEquipmentIdSet.has(equipment.id)}
                    onChange={() => toggleSelection(equipment.id)}
                  />
                ),
              },
              {
                property: 'id',
                size: '3%',
                render: (equipment: Equipment) => (
                  <Box pad={{ vertical: 'small' }}>
                    <Link to={`/clients/${equipment.client_id}/equipment/${equipment.id}`}>
                      <Box direction="row" round="6px" justify="center" align="center">
                        <Tip content={tooltips.viewEquipmentDetails}>
                          <SettingsOption size="16px" color="accent-1" />
                        </Tip>
                      </Box>
                    </Link>
                  </Box>
                ),
              },
              {
                property: 'equipment_type.name',
                size: '17%',
                header: <DataTableHeader title={t('equipment:chargingType')} />,
                render: (equipment: Equipment) => <DataTableItem value={equipment.equipment_type.name} />,
              },
              {
                property: 'is_active',
                align: 'center',
                header: <DataTableHeader title={t('common:status')} />,
                render: (equipment: Equipment) => (
                  <DataTableItem value={equipment.is_active ? EntityStatus.Active : EntityStatus.Retired} />
                ),
              },
              {
                property: 'is_metered',
                size: '8%',
                align: 'center',
                header: <DataTableHeader title={t('equipment:meter')} />,
                render: (equipment: Equipment) => (
                  <Box direction="row">{(equipment.is_metered && <Dashboard size="20px" />) || '-'}</Box>
                ),
              },
              {
                property: 'serial_number',
                size: '13%',
                align: 'center',
                header: <DataTableHeader title={t('equipment:serialVin')} />,
                render: (equipment: Equipment) => (
                  <DataTableItem
                    value={
                      equipment.serial_number?.length >= 22
                        ? `${equipment.serial_number?.slice(0, 4)}...${equipment.serial_number?.slice(-4)}`
                        : equipment.serial_number || '-'
                    }
                  />
                ),
              },
              {
                property: 'manufacturer',
                size: '12%',
                align: 'center',
                header: <DataTableHeader title={t('equipment:manufacturer')} />,
                render: (equipment: Equipment) => <DataTableItem value={equipment.manufacturer || '-'} />,
              },
              {
                property: 'model_year',
                size: '11%',
                align: 'center',
                header: <DataTableHeader title={t('equipment:modelYear')} />,
                render: (equipment: Equipment) => <DataTableItem value={equipment.model_year || '-'} />,
              },
              {
                property: 'model_number',
                size: '13%',
                align: 'center',
                header: <DataTableHeader title={t('equipment:modelNumber')} />,
                render: (equipment: Equipment) => <DataTableItem value={equipment.model_number || '-'} />,
              },
              {
                property: 'unit_number',
                size: '11%',
                align: 'center',
                header: <DataTableHeader title={t('equipment:unitNo')} />,
                render: (equipment: Equipment) => <DataTableItem value={equipment.unit_number || '-'} />,
              },
              {
                property: 'total_kwh',
                size: '12%',
                header: (
                  <DataTableHeader>
                    <Box align="center">
                      <Text size="small">{t('common:totalKwh')}</Text>
                      {currentUsagePeriod && (
                        <Text size="small">({getPeriodFromDateString(currentUsagePeriod, isCanada)})</Text>
                      )}
                    </Box>
                  </DataTableHeader>
                ),
                render: (equipment: Equipment) =>
                  equipment.current_reporting_period_equipment_usage?.total_kwh ? (
                    <DataTableItem
                      value={formatNumber(
                        equipment.current_reporting_period_equipment_usage.total_kwh,
                        true,
                        false,
                        false,
                        4
                      )}
                      unitName="kWh"
                    />
                  ) : (
                    <DataTableItem value="-" />
                  ),
              },
            ]}
            data={equipmentList}
            sortable
            background={['light-6', 'white']}
            border={{ color: 'light-2', side: 'bottom', size: 'small' }}
          />
        </Box>
      );
    };

    return isVisible ? (
      <Layer
        position="center"
        onEsc={() => setIsVisible(false)}
        onClickOutside={() => setIsVisible(false)}
        responsive={false}
        style={{
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '95vh',
          overflow: 'auto',
          borderRadius: '8px',
        }}
      >
        <FormCard
          width="100%"
          title={t('equipment:deleteEquipment')}
          icon={<EntityIcon entityName={ModelName.Equipment} />}
          onSubmit={() => deleteEquipment()}
          isLoading={isLoading || isDeleting}
          hideRequiredText
          saveButtonLabel="DELETE"
          saveButtonLoadingLabel="DELETING..."
          saveButtonDisabled={!isConfirmed || !selectedEquipmentIds.length}
          headerChildren={[
            <Button
              key="close"
              pad="none"
              onClick={() => setIsVisible(false)}
              icon={<Close size="20px" />}
              tip={t('common:close')}
            />,
          ]}
        >
          <Box pad={{ horizontal: 'medium', bottom: 'medium' }} gap="medium">
            <InfoBox gap="1rem" direction="row" align="center">
              <StatusWarning size="medium" color="status-warning" />
              <Text>
                {t(
                  'equipment:deleteWarning',
                  'Warning: This action will permanently delete the selected equipment. This action cannot be undone.'
                )}
              </Text>
            </InfoBox>
            <Box direction="row" align="end" justify="between">
              {hasSelection && (
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed} onChange={() => setIsConfirmed(!isConfirmed)} color="white" />
                  <Text color="white">
                    {t('equipment:deleteMultipleConfirmation', { count: selectedEquipmentIds.length })}
                  </Text>
                </InfoBox>
              )}
              <Box direction="row" align="center" gap="small" margin={{ left: 'auto' }}>
                {hasSelection && (
                  <Text size="small">{t('common:selectedCount', { count: selectedEquipmentIds.length })}</Text>
                )}
                {(hasSelection || !!metadata?.total) && (
                  <AddButton
                    label={hasSelection ? t('common:clearAll') : `${t('common:selectAll')} (${metadata?.total})`}
                    onClick={() => {
                      setIsConfirmed(false);
                      if (hasSelection) setSelectedEquipmentIds([]);
                      else selectAll();
                    }}
                    disabled={isSelectingAll || isLoading || isSearching}
                  />
                )}
                <SearchInput
                  searchQuery={searchQuery}
                  placeholder={t('equipment:searchBySerial')}
                  setSearchQuery={setSearchQuery}
                  onSearch={search}
                  isSearching={isSearching}
                />
              </Box>
            </Box>
            <Box gap="small">
              {isLoading ? (
                <Box pad={{ vertical: 'medium' }} height="10vh" fill="horizontal" justify="center" align="center">
                  <Spinner size="medium" />
                </Box>
              ) : equipmentList.length ? (
                <>
                  {renderDataTable()}
                  {metadata && (
                    <Box pad={{ top: 'medium' }} justify="center" align="center">
                      <Pagination
                        alignSelf="center"
                        size="small"
                        page={currentPage}
                        step={metadata.per_page}
                        numberItems={metadata.total}
                        onChange={(e) => setCurrentPage(e.page)}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box pad={{ horizontal: 'small', vertical: 'medium' }} background="light-6" align="center">
                  <Text size="medium" fontFamily="Lato, sans-serif">
                    {t('equipment:noEquipmentFound', 'No equipment found.')}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </FormCard>
      </Layer>
    ) : (
      <></>
    );
  }
);
